import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';

import {
  CustomCustomerField,
  OrganizationConfig
} from '../../../../../../../models/organization-config';
import {
  CustomerProfileActionButton,
  CustomerProfileSidebarCard,
  ProfileField
} from '../../../../../../../models/organization-config-enums';
import { AccountService } from '../../../../../../../services/account/account.service';
import {
  getCustomerFieldLabel,
  getCustomerProfileActionButtonLabel,
  getCustomerProfileSidebarCardLabel,
  reconcileActionButtonOrder,
  reconcileSidebarOrder,
  usesSpecificAllthenticateProducts
} from '../../../../../../../utils-models';

interface SidebarSampleRow {
  left: string;
  right?: string;
}


@Component({
  selector: 'app-customer-profile-preview-modal',
  templateUrl: './customer-profile-preview-modal.component.html',
  styleUrls: ['./customer-profile-preview-modal.component.css']
})
export class CustomerProfilePreviewModalComponent {
  @Input() organizationConfig: OrganizationConfig;
  @Input() customCustomerFields: CustomCustomerField[];

  public CustomerProfileSidebarCard = CustomerProfileSidebarCard;
  public getCustomerProfileSidebarCardLabel = getCustomerProfileSidebarCardLabel;
  public getCustomerProfileActionButtonLabel = getCustomerProfileActionButtonLabel;

  constructor(
    public activeModal: NgbActiveModal,
    private accountService: AccountService
  ) {}

  public get sidebarOrder(): CustomerProfileSidebarCard[] {
    const order = reconcileSidebarOrder(
      this.organizationConfig.customer_profile_sidebar_order
    );

    // Matches the gate on the profile itself, so the preview does not offer a
    // card the org will never see.
    if (this.usesSpecificAllthenticateProducts()) {
      return order;
    }

    return order.filter(
      card => card !== CustomerProfileSidebarCard.allthenticateStatus
    );
  }

  public usesSpecificAllthenticateProducts(): boolean {
    return usesSpecificAllthenticateProducts(this.organizationConfig);
  }

  public get actionButtonOrder(): CustomerProfileActionButton[] {
    return reconcileActionButtonOrder(this.organizationConfig.customer_profile_action_button_order);
  }

  public getMockSidebarRows(card: CustomerProfileSidebarCard): SidebarSampleRow[] {
    switch (card) {
      case CustomerProfileSidebarCard.emergencyContacts:
        return [{ left: 'Nala Cornfield, <i>Spouse</i>', right: '720-444-5555' }];
      case CustomerProfileSidebarCard.medicalInfo:
        return [{ left: 'Medical Notes', right: 'Added Apr 12, 2026' }];
      case CustomerProfileSidebarCard.proficiency:
        return [
          { left: 'Top-rope Belay', right: 'Expires Aug 1, 2026' },
          { left: 'Lead Climb', right: 'Expires Aug 1, 2026' },
        ];
      case CustomerProfileSidebarCard.notes:
        return [{ left: 'Prefers afternoon sessions', right: 'May 3, 2026' }];
      case CustomerProfileSidebarCard.documents:
        return [{ left: 'Liability Waiver', right: 'Expires Aug 1, 2026' }];
      case CustomerProfileSidebarCard.challenges:
        return [{ left: 'Summer Sender', right: '12/20' }];
      case CustomerProfileSidebarCard.linkedAccounts:
        return [{ left: 'Nala Cornfield', right: 'Spouse' }];
      case CustomerProfileSidebarCard.associations:
        return [{ left: 'Youth Climbing Team' }];
      case CustomerProfileSidebarCard.tags:
        return [{ left: 'VIP' }, { left: 'Coach' }];
      case CustomerProfileSidebarCard.allthenticateStatus:
        return [
          { left: 'Account', right: 'Active' },
          { left: 'Last Invite', right: 'Aug 1, 2026' },
        ];
      default:
        return [];
    }
  }

  public customerName() {
    return 'Simba Cornfield';
  }

  public customerAgeAndBirthday() {
    const birthday = moment('2015-11-13', 'YYYY-MM-DD');
    const birthdayStr = birthday.format('ll');

    const age = moment().diff(birthday, 'years');
    const ageStr = age.toString();

    return ageStr + ' / ' + birthdayStr;
  }

  /**
   * Get the customer's value for the given profile field.
   */
  public getFieldValue(field: ProfileField) {
    switch (field) {
      case ProfileField.name:
        return this.customerName();
      case ProfileField.preferred_name:
        return 'King Simba';
      case ProfileField.email:
        return 'simba@hellocapitan.com';
      case ProfileField.emailIsOptedIn:
        return 'Yes';
      case ProfileField.telephone:
        return '720-222-3333';
      case ProfileField.age_birthday:
        return this.customerAgeAndBirthday();
      case ProfileField.street_address:
        return '123 Main St, Unit 1';
      case ProfileField.city:
        return 'Denver';
      case ProfileField.state:
        return 'Colorado';
      case ProfileField.zipcode:
        return '80202';
      case ProfileField.country:
        return 'US';
      case ProfileField.gym_status:
        return 'Member, Recurring';
      case ProfileField.membership_status:
        return 'Member until ' + moment('2022-12-04', 'YYYY-MM-DD').format('ll');
      case ProfileField.punchpass_status:
        return 'None';
      case ProfileField.guest_passes:
      case ProfileField.guest_passes_all_customers:
        return '3 remaining';
      case ProfileField.capitan_status:
        return 'Active';
      case ProfileField.id_number:
        return '235259246';
      case ProfileField.last_check_in:
        return moment('2020-12-04', 'YYYY-MM-DD').format('ll');
      case ProfileField.check_in_count:
        return '63';
      case ProfileField.gender:
        return 'Male';
      case ProfileField.pronouns:
        return 'He/Him';
      case ProfileField.barcode:
        return '0123456789';
      case ProfileField.homeLocation:
        return this.accountService.getDefaultLocation().name;
      case ProfileField.referralCode:
        return '34567890';
      default:
        return 'Value';
    }
  }

  public getThisCustomerFieldLabel(profileField: ProfileField) {
    return getCustomerFieldLabel(profileField, this.customCustomerFields);
  }
}
